import { useMutation, useQuery } from "@apollo/client";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import { FETCH_RESERVATION as fetchReservation } from "../../../../graphql/query/Reservation";
import { UPDATE_RESERVATION as updateReservationMutation } from "../../../../graphql/mutation/Reservation";
import AuthRoute from "../../../../components/AuthRoute";
import routes from "./routes";
import { FormSaveStatus, NavBar } from "../../../../components/ui";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";
import { Portal, getChangedFields } from "../../../../util";

const Body = props => {
  return (
    <RouterRoutes>
      {routes
        ?.filter?.(item => item?.element)
        ?.map((item, index) => {
          return (
            <Route
              key={index}
              path={item?.href}
              element={
                <AuthRoute permissions={item.permissions}>
                  <item.element {...props} />
                </AuthRoute>
              }
            />
          );
        })}
    </RouterRoutes>
  );
};

const reservationReadOnlyKeys = [
  "createdBy",
  "dateModified",
  "instance",
  "isnewData",
  "rawFields",
  "reservation",
  "reservationInstances",
  "reservationUser",
  "type",
  "userId",
  "updatedAt",
  "reservationInstanceCount",
  "__typename",
];

const omitKeys = (object, keys) =>
  Object.fromEntries(
    Object.entries(object).filter(([key]) => !keys.includes(key)),
  );

const ReservationForm = props => {
  const { id } = useParams();
  const { data: { reservation } = {} } = useQuery(
    fetchReservation({
      createdBy: {},
      reservationInstance: {
        instance: {
          edition: {
            artwork: {
              artists: {},
            },
          },
        },
      },
      reservationUser: {},
      type: {},
    }),
    {
      variables: { id: +id },
    },
  );

  const [updateReservation, { loading }] = useMutation(
    updateReservationMutation({
      createdBy: {},
      reservationInstance: {},
      reservationUser: {},
      type: {},
    }),
  );

  const saveReservationChanges = async (values, savedValues) => {
    const changedFields = getChangedFields(values, savedValues);

    if (Object.keys(changedFields).length > 0) {
      const { data } = await updateReservation({
        variables: {
          input: {
            id: +id,
            ...values,
          },
        },
      });

      if (!data?.updateReservation?.success) {
        throw new Error(
          data?.updateReservation?.error ||
            "The reservation could not be saved",
        );
      }
    }
  };

  const updateReservationValue = values =>
    saveReservationChanges(values, reservation);

  const formik = useAutosaveFormik({
    initialValues: reservation,
    onSave: (values, savedValues) =>
      saveReservationChanges(
        omitKeys(values, reservationReadOnlyKeys),
        savedValues,
      ),
  });

  const bodyProps = {
    ...props,
    reservation,
    formik,
    loading,
    updateReservation,
    updateReservationValue,
  };

  return (
    <div className="relative flex min-h-full flex-col lg:flex-row">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="m-4 flex min-w-0 flex-1 overflow-x-auto lg:m-8 lg:mr-[80px]">
        {reservation && <Body {...bodyProps} />}
      </div>
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  return <ReservationForm key={id} {...props} />;
};

export default Wizard;
